import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Plane, MapPin, Utensils, Camera, Hotel, Check, Mountain } from "lucide-react";
import SEO, { pageSEO, schemas } from "../components/SEO";

const itinerary = [
  {
    day: "Night 0",
    title: "Arrival in Delhi",
    overview: "Late night arrival at Delhi; your journey begins.",
    activities: [
      "Private VIP-style transfer in a premium vehicle directly to your 5-star Delhi hotel",
      "Porterage and in-room check-in where possible",
      "Overnight in Delhi"
    ],
    iconType: "plane"
  },
  {
    day: "Day 1",
    title: "Delhi: First Impressions, Connaught Place & Cultural Evening",
    overview: "Orient yourself to India's vibrant capital with iconic sights and cultural immersion.",
    activities: [
      "Leisurely breakfast at the hotel",
      "Late morning: drive through Lutyens' Delhi (India Gate, Parliament area, Presidential residence exteriors) with commentary",
      "Visit to Connaught Place, the heart of Delhi, featuring British-era Georgian-style buildings of the inner and outer circles",
      "Late lunch at Nexus Select CityWalk Mall (included)",
      "Evening: curated cultural show (music/dance/theatre) at a quality venue",
      "Overnight in Delhi"
    ],
    iconType: "mappin"
  },
  {
    day: "Day 2",
    title: "Delhi: Old City, Red Fort & Bukhara Dinner",
    overview: "Explore the historic heart of Old Delhi and dine at one of India's most celebrated restaurants.",
    activities: [
      "Breakfast at the hotel",
      "Morning: Visit inside the Red Fort with contextual storytelling on Mughal history",
      "Guided exploration of Old Delhi, including Chandni Chowk lanes",
      "Visit to Central Cottage Industries Emporium (Government-owned arts and crafts center)",
      "Afternoon: visit Qutub Minar or Lodhi Art District with photography focus",
      "Evening: included signature dinner at Bukhara, ITC Maurya — North West Frontier cuisine",
      "Overnight in Delhi"
    ],
    iconType: "utensils"
  },
  {
    day: "Day 3",
    title: "Delhi → Agra: Sunset Taj Perspective",
    overview: "Journey to Agra and experience your first magical view of the Taj Mahal at sunset.",
    activities: [
      "Breakfast at the hotel",
      "Late morning: private drive to Agra in a high-end vehicle",
      "Lunch on way at rest stop (included)",
      "Check into a 5-star hotel, ideally with Taj views",
      "Late afternoon: guided visit to Mehtab Bagh for a tranquil sunset view of the Taj Mahal",
      "Evening free for relaxed in-house dining (dinner on own)",
      "Overnight in Agra"
    ],
    iconType: "camera"
  },
  {
    day: "Day 4",
    title: "Agra → Jaipur: Sunrise Taj & Stepwell Stop",
    overview: "Witness the Taj Mahal at sunrise, then journey to the Pink City of Jaipur.",
    activities: [
      "Early breakfast or packed snack from the hotel",
      "Sunrise: in-depth guided visit inside the Taj Mahal with emphasis on symbolism and craftsmanship",
      "Late morning: Agra Fort (and optionally Itmad-ud-Daulah) with connecting stories",
      "Afternoon: scenic drive to Jaipur with stop at Abhaneri stepwell",
      "Lunch on way at rest stop (included)",
      "Check into a palace hotel in Jaipur for an immersive royal heritage stay",
      "Overnight in Jaipur"
    ],
    iconType: "camera"
  },
  {
    day: "Day 5",
    title: "Jaipur: Amber Fort, Old City & Suvarna Mahal Dinner",
    overview: "Experience the grandeur of Rajasthan's royal heritage.",
    activities: [
      "Breakfast at the palace hotel",
      "Morning: elephant ride experience at Amber Fort (ethical operators, timed to avoid heat)",
      "Guided exploration of Amber Fort's intimate courtyards",
      "Afternoon: City Palace and Jantar Mantar with narrative focus on royal life",
      "Optional time in the old city bazaars",
      "Evening: included dinner at Suvarna Mahal, Rambagh Palace — Taj's flagship Indian fine dining",
      "Overnight in Jaipur"
    ],
    iconType: "hotel"
  },
  {
    day: "Day 6",
    title: "Jaipur: Palace Leisure & Optional Cooking Lesson",
    overview: "A gentler day to enjoy the palace amenities and explore at your own pace.",
    activities: [
      "Leisurely breakfast at the palace hotel",
      "Car and driver at your disposal for 4 hours (included)",
      "Flexible time: enjoy palace hotel amenities (spa, gardens, pool)",
      "Or guided, interest-based exploration (photography, textiles, architecture)",
      "Optional included one-hour hands-on cooking lesson with a local chef",
      "Evening free (dinner on own with concierge recommendations)",
      "Overnight in Jaipur"
    ],
    iconType: "utensils"
  },
  {
    day: "Day 7",
    title: "Jaipur → Rishikesh: Into the Himalayan Foothills",
    overview: "Travel to the serene Himalayan foothills and witness the sacred Ganga Aarti.",
    activities: [
      "Breakfast at the palace hotel",
      "Morning: transfer to Jaipur airport; flight to Dehra Dun",
      "Private transfer to 5-star Rishikesh area hotel (Taj Rishikesh Resort & Spa or comparable)",
      "Late afternoon: gentle riverside orientation walk and tea (included)",
      "Evening: attend Ganga Aarti at a thoughtfully selected ghat",
      "Overnight in Rishikesh"
    ],
    iconType: "mountain"
  },
  {
    day: "Day 8",
    title: "Rishikesh: Yoga & Ana Rasa Dinner",
    overview: "Embrace wellness and spirituality in the yoga capital of the world.",
    activities: [
      "Breakfast at the hotel",
      "Morning: private small group yoga session tailored for all levels",
      "Late morning: visit a respected ashram/spiritual center with conversation with a teacher",
      "Afternoon: easy guided nature walk to a nearby waterfall or viewpoint",
      "Evening: included dinner at Ana Rasa, Anand Kashi by the Ganges",
      "Overnight in Rishikesh"
    ],
    iconType: "mountain"
  },
  {
    day: "Day 9",
    title: "Rishikesh: Choose Your Own Depth",
    overview: "Personalize your experience with wellness activities of your choice.",
    activities: [
      "Breakfast at the hotel",
      "Day options (pre-chosen before departure):",
    ],
    subOptions: [
      "Deeper yoga/meditation immersion",
      "Ayurvedic consultation and treatments at a vetted center",
      "Spa session (additional cost)"
    ],
    activitiesAfter: [
      "Early evening: transfer to Dehra Dun airport; flight to Delhi",
      "Overnight in Delhi"
    ],
    iconType: "mountain"
  },
  {
    day: "Day 10",
    title: "Delhi: Homeward Bound",
    overview: "Final moments in India before your journey home.",
    activities: [
      "Farewell breakfast at the hotel",
      "If your flight is in the evening, choose how to spend your final hours:",
    ],
    subOptions: [
      "Shopping: Discover local shops and find souvenirs at your own pace",
      "Relax at Hotel: Unwind at the hotel and enjoy the amenities",
      "Explore: Pursue personal interests with some free-form exploration"
    ],
    activitiesAfter: [
      "Evening: Private transfer to Indira Gandhi International Airport",
      "Assistance with check-in as needed",
      "Depart India — until we meet again!"
    ],
    iconType: "plane"
  }
];

const iconMap = {
  plane: Plane,
  mappin: MapPin,
  utensils: Utensils,
  camera: Camera,
  hotel: Hotel,
  mountain: Mountain
};

export default function ItineraryPage() {
  return (
    <div className="pt-24 md:pt-32" data-testid="itinerary-page">
      <SEO {...pageSEO.itinerary} />
      {/* Hero */}
      <section className="section-padding bg-[#243b5a] text-white">
        <div className="container-max text-center">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">10-Day Journey</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            Day by Day <span className="text-[#D4AF37]">Itinerary</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Every day carefully crafted to balance iconic experiences with moments of reflection and comfort.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="section-padding bg-white border-b border-[#D4AF37]/10">
        <div className="container-max">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-serif text-2xl text-[#243b5a] mb-6 text-center">What's Included Every Day</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Hotel, text: "5-star or palace level accommodation" },
                { icon: Utensils, text: "Daily breakfast at all properties" },
                { icon: MapPin, text: "Private transport and local guides as noted" }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 bg-[#F9F9F7] p-4 rounded-lg">
                  <item.icon className="w-5 h-5 text-[#D4AF37]" />
                  <span className="text-[#4A4A4A] text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Itinerary Accordion */}
      <section className="section-padding bg-[#F9F9F7]">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4" defaultValue="item-0">
              {itinerary.map((day, index) => {
                const IconComponent = iconMap[day.iconType];
                return (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`}
                    className="bg-white rounded-xl border border-[#D4AF37]/20 overflow-hidden shadow-soft"
                    data-testid={`itinerary-day-${index}`}
                  >
                    <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-[#F9F9F7]/50">
                      <div className="flex items-center gap-4 text-left">
                        <div className="w-14 h-14 rounded-full bg-[#243b5a] flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <span className="text-[#D4AF37] text-sm font-medium">{day.day}</span>
                          <h3 className="font-serif text-xl text-[#243b5a]">{day.title}</h3>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className="pl-[4.5rem]">
                        <p className="text-[#4A4A4A] mb-4 italic">{day.overview}</p>
                        <ul className="space-y-3">
                          {day.activities.map((activity, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <Check className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                              <span className="text-[#4A4A4A]">{activity}</span>
                            </li>
                          ))}
                          {day.subOptions && day.subOptions.map((option, i) => (
                            <li key={`sub-${i}`} className="flex items-start gap-3 ml-8">
                              <span className="w-2 h-2 rounded-full bg-[#D4AF37] mt-2 flex-shrink-0" />
                              <span className="text-[#4A4A4A]">{option}</span>
                            </li>
                          ))}
                          {day.activitiesAfter && day.activitiesAfter.map((activity, i) => (
                            <li key={`after-${i}`} className="flex items-start gap-3">
                              <Check className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                              <span className="text-[#4A4A4A]">{activity}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-max text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mb-6">
            Ready to <span className="text-[#D4AF37]">Reserve Your Spot</span>?
          </h2>
          <p className="text-[#4A4A4A] text-lg max-w-2xl mx-auto mb-8">
            See pricing details and available departure dates.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing">
              <Button className="btn-primary" data-testid="view-pricing-btn">
                View Pricing & Dates
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="btn-secondary" data-testid="contact-us-btn">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
